import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { AuthGate } from "@/components/layout/AuthGate";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const InvoiceDetail = lazy(() => import("@/pages/InvoiceDetail"));
const Workflows = lazy(() => import("@/pages/Workflows"));
const WorkflowDetail = lazy(() => import("@/pages/WorkflowDetail"));
const WorkflowBuilder = lazy(() => import("@/pages/WorkflowBuilder"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const SupplierDetail = lazy(() => import("@/pages/SupplierDetail"));
const PaymentRuns = lazy(() => import("@/pages/PaymentRuns"));
const PaymentRunDetail = lazy(() => import("@/pages/PaymentRunDetail"));
const PaymentRunBuilder = lazy(() => import("@/pages/PaymentRunBuilder"));
const BankAccounts = lazy(() => import("@/pages/BankAccounts"));
const PurchaseOrders = lazy(() => import("@/pages/PurchaseOrders"));
const Budgets = lazy(() => import("@/pages/Budgets"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Settings = lazy(() => import("@/pages/Settings"));
const Users = lazy(() => import("@/pages/Users"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Compliance = lazy(() => import("@/pages/Compliance"));
const WorkflowResolver = lazy(() => import("@/pages/WorkflowResolver"));
const ApprovalConfirmation = lazy(() => import("@/pages/ApprovalConfirmation"));
const Login = lazy(() => import("@/pages/Login"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const CallbackPage = lazy(() => import("@/pages/auth/CallbackPage"));

function LoadingView() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-2xl space-y-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

/**
 * RootRedirect — schickt den User auf seinen aktiven Workspace-Dashboard.
 * Liefert WorkspaceContext keinen activeWorkspaceId → AuthGate kuemmert
 * sich um den Hub-Roundtrip.
 */
function RootRedirect() {
  const { activeWorkspaceId, isLoading } = useActiveWorkspace();
  if (isLoading) return <LoadingView />;
  if (!activeWorkspaceId) {
    // AuthGate auf den geschuetzten Routes zeigt LoadingScreen + Hub-Redirect
    // (kein User → redirectToHub, kein Membership → redirectToHub). Hier auf
    // /workspaces leiten triggert genau diese Logik.
    return <Navigate to="/workspaces" replace />;
  }
  return <Navigate to={`/workspaces/${activeWorkspaceId}/dashboard`} replace />;
}

export default function App() {
  return (
    <ErrorBoundary scope="app">
      <Suspense fallback={<LoadingView />}>
        <Routes>
          {/* OIDC callback routes — must come BEFORE AuthGate-protected routes */}
          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/auth/silent-renew" element={<div />} />
          <Route path="/login" element={<Login />} />
          {/* Public Approval Confirmation — Signed-Link-Bestaetigung ohne Login */}
          <Route path="/approval-confirmation" element={<ApprovalConfirmation />} />
          <Route
            path="/"
            element={
              <AuthGate>
                <RootRedirect />
              </AuthGate>
            }
          />
          <Route
            path="/workspaces"
            element={
              <AuthGate>
                <RootRedirect />
              </AuthGate>
            }
          />
          <Route
            path="/workspaces/:workspaceId"
            element={
              <AuthGate>
                <AppShell />
              </AuthGate>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:invoiceId" element={<InvoiceDetail />} />
            <Route path="workflows" element={<Workflows />} />
            <Route path="workflows/new" element={<WorkflowBuilder />} />
            <Route path="workflows/workflow-resolver" element={<WorkflowResolver />} />
            <Route path="workflows/:workflowId" element={<WorkflowDetail />} />
            <Route path="workflows/:workflowId/edit" element={<WorkflowBuilder />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/:supplierId" element={<SupplierDetail />} />
            <Route path="payment-runs" element={<PaymentRuns />} />
            <Route path="payment-runs/new" element={<PaymentRunBuilder />} />
            <Route path="payment-runs/:runId" element={<PaymentRunDetail />} />
            <Route path="bank-accounts" element={<BankAccounts />} />
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route path="budgets" element={<Budgets />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<Settings />} />
            <Route path="users" element={<Users />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="compliance" element={<Compliance />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        <CommandPalette />
      </Suspense>
    </ErrorBoundary>
  );
}
